import * as React from 'react';

import { Pill, Text } from '@components/common';
import { CheckmarkInCircleSVG, PencilSVG, SkipLogicSVG, SubtractCircleSVG } from '@components/svgs';

import { RuleLogicSummary } from '../RuleSummary/RuleLogicSummary';
import { RuleOutcomeSummary } from '../RuleSummary/RuleOutcomeSummary';

import { AllLogicEmptyState } from './AllLogicEmptyState';

interface RuleGroup {
  title: string;
  rules: AnswerTagLogic[];
  color: 'green' | 'red' | 'blue';
}

interface Props {
  screener: Screener;
  onEditRule?: (question: ScreenerField) => void;
}

export const AllLogicTab: React.FC<Props> = ({ screener, onEditRule }) => {
  const getRuleGroups = React.useMemo(() => {
    const allRules: { rule: AnswerTagLogic; field: ScreenerField }[] = [];

    screener.fields.forEach((field) => {
      const answerTagLogic = field.answer_tag_logic || [];
      answerTagLogic.forEach((rule) => {
        allRules.push({ rule, field });
      });
    });

    const idealRules = allRules.filter(({ rule }) => rule.tag_type === 'ideal');
    const disqualifyingRules = allRules.filter(
      ({ rule }) => rule.tag_type === 'disqualifying' || rule.tag_type === 'ignore_disqualified'
    );
    const skipToRules = allRules.filter(({ rule }) => rule.tag_type === 'skip_to');

    const groups: RuleGroup[] = [
      {
        title: 'Qualifying answers',
        rules: idealRules.map(({ rule }) => rule),
        color: 'green'
      },
      {
        title: 'Disqualifying answers',
        rules: disqualifyingRules.map(({ rule }) => rule),
        color: 'red'
      },
      {
        title: 'Conditional branches',
        rules: skipToRules.map(({ rule }) => rule),
        color: 'blue'
      }
    ];

    return groups;
  }, [screener]);

  const getQuestionForRule = (rule: AnswerTagLogic): ScreenerField | undefined => {
    return screener.fields.find((field) => field.answer_tag_logic?.some((r) => r === rule));
  };

  const getGroupRuleCount = (groupRules: AnswerTagLogic[]): number => {
    return groupRules.length;
  };

  const getGroupIcon = (groupTitle: string) => {
    switch (groupTitle) {
      case 'Qualifying answers':
        return <CheckmarkInCircleSVG className='h-3 w-3' />;
      case 'Disqualifying answers':
        return <SubtractCircleSVG className='h-3 w-3' />;
      case 'Conditional branches':
        return <SkipLogicSVG className='h-3 w-3' />;
      default:
        return <CheckmarkInCircleSVG className='h-3 w-3' />;
    }
  };

  const getEmptyStateMessage = (groupTitle: string) => {
    switch (groupTitle) {
      case 'Qualifying answers':
        return 'Mark answers as qualifying at the question level to review here.';
      case 'Disqualifying answers':
        return 'Mark answers as disqualifying at the question level to review here.';
      case 'Conditional branches':
        return 'Create conditional branches at the question level to review here.';
      default:
        return '';
    }
  };

  const hasNoRules = getRuleGroups.every((group) => group.rules.length === 0);

  if (hasNoRules) {
    return (
      <div className='p-6'>
        <AllLogicEmptyState />
      </div>
    );
  }

  return (
    <div className='space-y-6 p-6'>
      {getRuleGroups.map((group, groupIndex) => (
        <div key={groupIndex} className='rounded-lg border border-gray-200 p-4'>
          <div className='mb-4 flex items-center gap-2'>
            <Text className='text-sm' bold>
              {group.title}
            </Text>
            <Pill
              color={getGroupRuleCount(group.rules) === 0 ? 'gray' : group.color}
              className='flex items-center gap-1 space-x-1'
            >
              {getGroupIcon(group.title)}
              {getGroupRuleCount(group.rules)}
            </Pill>
          </div>

          <div className='space-y-2'>
            {group.rules.length > 0 ? (
              group.rules.map((rule, ruleIndex) => {
                const question = getQuestionForRule(rule);
                if (!question) return null;

                return (
                  <div key={ruleIndex} className='space-y-2 rounded-lg bg-gray-50 p-3 text-xs'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center'>
                        <RuleLogicSummary
                          question={question}
                          logic={{
                            operator: rule.operator,
                            answer_value: rule.answer_value
                          }}
                        />
                      </div>
                      {onEditRule && (
                        <button
                          onClick={() => onEditRule(question)}
                          className='flex h-6 w-6 items-center justify-center rounded-full transition-colors duration-200 hover:bg-gray-200'
                          title='Edit question logic'
                        >
                          <PencilSVG className='h-4 w-4 text-gray-400' />
                        </button>
                      )}
                    </div>
                    {rule.tag_type === 'skip_to' && (
                      <div className='flex items-center space-x-2'>
                        <strong>Then</strong>
                        <RuleOutcomeSummary
                          outcome={{ tag_value: rule.tag_value, tag_type: rule.tag_type }}
                          questions={screener.fields}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <Text className='text-xs text-gray-500'>{getEmptyStateMessage(group.title)}</Text>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
